import type React from "react";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import {ChevronRightOutlined, PaymentsOutlined, ReceiptLongOutlined, SavingsOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {
    harmonizationCalculation,
    type PtV2HarmonizationView,
    termMixShares,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {
    formatEur,
    formatEurFull,
    formatInt,
    formatRatio,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2SourcesQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2Citation} from "~/experiments/pages/payment-terms-v2/components/PtV2Citation.tsx";
import {PtV2TermTag} from "~/experiments/pages/payment-terms-v2/components/PtV2TermTag.tsx";

const HARMONIZATION_SLUGS = ['dpo_harmonization_targets', 'pt_term_harmonization'];

/** Drawer body for one harmonization supplier at the selected target (per supplier, or one term for all). */
export const PtV2HarmonizationDetail: React.FC<{row: PtV2HarmonizationView}> = ({row}) => {
    const sourcesQ = usePtV2SourcesQuery();
    const tables = sourcesQ.data?.workpapers.find(w => HARMONIZATION_SLUGS.includes(w.slug))?.sourceTables ?? [];
    const target = row.targetCode ?? 'the target';
    const mix = termMixShares(row.termMix ?? []);
    const calc = harmonizationCalculation(row);
    return <div className="ptv2-detail">
        <div className="ptv2-chips">
            <PtV2TermTag code={row.currentTerm}/>
            <ChevronRightOutlined fontSize="small" className="ptv2-faint"/>
            {row.targetCode && <PtV2TermTag code={row.targetCode}/>}
            {row.shiftDays > 0
                ? <Tag color="success" variant="outlined" label={`+${row.shiftDays} days DPO`}/>
                : <Tag color="default" variant="outlined" label="Already at target"/>}
            {row.negotiationTier && <Tag color="info" variant="outlined" label={row.negotiationTier}/>}
        </div>
        <SitKpiHeader items={[
            {tone: 'ok', icon: <SavingsOutlined/>, label: 'Freed', value: formatEur(row.wcFreed)},
            {tone: 'plain', icon: <PaymentsOutlined/>, label: 'Spend', value: formatEur(row.spend)},
            {tone: 'info', icon: <ReceiptLongOutlined/>, label: 'Invoices', value: formatInt(row.invoiceCount)},
        ]}/>
        <p>
            {row.shiftDays > 0
                ? <>Moving {row.supplier} from {row.currentTerm} to {target} extends payment by {row.shiftDays} days and
                    frees {formatEurFull(row.wcFreed)} of working capital. This needs a renegotiated term with the supplier.</>
                : <>{row.supplier} is already at or beyond {target}.</>}
        </p>
        <dl className="ptv2-kv">
            <dt>Category</dt><dd>{row.category ?? 'Not stated in the analysis'}</dd>
            {row.targetTerm && <><dt>Workpaper target</dt>
                <dd>
                    <b className="ptv2-mono">{row.targetTerm}</b>
                    {row.liquidityUnlock != null && <span className="ptv2-muted"> · unlocks {formatEurFull(row.liquidityUnlock)}</span>}
                </dd></>}
            {row.negotiationTier && <><dt>Negotiation tier</dt><dd>{row.negotiationTier}</dd></>}
            {row.wcSource === 'computed'
                ? <><dt>Calculation</dt><dd className="ptv2-mono">{calc.formula}</dd></>
                : <><dt>Working capital</dt>
                    <dd>
                        <b className="ptv2-num">{formatEurFull(calc.value)}</b>{' '}
                        <span className="ptv2-muted">{calc.label}</span>
                    </dd>
                    <dt>Cross-check</dt><dd className="ptv2-mono">{calc.formula}</dd></>}
            <dt>Sources</dt>
            <dd className="ptv2-chips">
                {tables.length ? tables.map(t => <PtV2Citation key={t}>{t}</PtV2Citation>) : <span className="ptv2-muted">{HARMONIZATION_SLUGS[0]}</span>}
            </dd>
        </dl>
        {mix.length > 0 && <>
            <div className="ptv2-eyebrow">Terms applied on this supplier’s invoices</div>
            <Table size="small" aria-label="Term mix">
                <TableHead>
                    <TableRow>
                        <TableCell>Term</TableCell>
                        <TableCell align="right">Share</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Invoices</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {mix.map((m, i) => <TableRow key={`${m.termCode}-${i}`}>
                        <TableCell>{m.termCode ? <PtV2TermTag code={m.termCode}/> : <span className="ptv2-faint">Not set</span>}</TableCell>
                        <TableCell align="right" className="ptv2-num">{formatRatio(m.share, 0)}</TableCell>
                        <TableCell align="right" className="ptv2-num">{formatEur(m.spend)}</TableCell>
                        <TableCell align="right" className="ptv2-num">{formatInt(m.invoiceCount)}</TableCell>
                    </TableRow>)}
                </TableBody>
            </Table>
        </>}
    </div>;
};
